/** The public home page: what BrainLog does, in plain words, and what it costs. */
import { useEffect, useState } from "react";
import { ArrowRight, Check, FileSearch, GraduationCap, MonitorPlay, PlayCircle, Users } from "lucide-react";
import { APP_TAGLINE, PLANS, STUDENT_DISCOUNT, type Plan } from "@brainlog/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useConfig, useMe } from "@/lib/queries";
import { choosePlan } from "@/features/settings/PlanSettings";
import { PublicFooter, PublicHeader, go } from "./PublicLayout";
import { track } from "@/lib/analytics";
import { OfferBanner } from "@/features/settings/offer";
import { AI_INTRO, AI_PAID, AI_WORDS, FAQ, FEATURES, FEATURES_INTRO, FEATURES_MORE, HERO, PRICING_INTRO, YOUTUBE_CHANNEL, YOUTUBE_TOUR } from "./content";

function price(plan: Plan, yearly: boolean) {
  if (plan.monthly === 0) return { main: "€0", sub: "forever" };
  if (plan.perPerson) return { main: `€${Math.round((plan.yearly ?? 0) / 12)}`, sub: "per person / month, billed yearly" };
  if (yearly) return { main: `€${Math.round((plan.yearly ?? 0) / 12)}`, sub: `per month · €${plan.yearly} a year` };
  return { main: `€${plan.monthly}`, sub: "per month" };
}

export function Pricing({ compact = false, current }: { compact?: boolean; current?: string | null }) {
  const [yearly, setYearly] = useState(true);
  // The daily included AI is only promised when this server has an AI key of its own.
  const aiIncluded = !!useConfig().data?.aiIncluded;
  const me = useMe();
  const shown = (f: string) => aiIncluded || !/AI actions a day included/.test(f);
  const plans = PLANS.filter((p) => p.id !== "lab");
  const lab = PLANS.find((p) => p.id === "lab")!;
  return (
    <div className="space-y-6">
      <div className="flex justify-center">
        <div role="group" aria-label="Billing period" className="inline-flex rounded-full border bg-card p-1 text-sm shadow-sm">
          {[
            [true, "Yearly (save over 20%)"],
            [false, "Monthly"],
          ].map(([v, label]) => (
            <button
              key={String(v)}
              type="button"
              aria-pressed={yearly === v}
              onClick={() => {
                setYearly(v as boolean);
                track("pricing_period", { period: v ? "year" : "month" });
              }}
              className={cn("rounded-full px-4 py-1.5 transition-colors", yearly === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
            >
              {label as string}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((p) => {
          const pr = price(p, yearly);
          return (
            <article
              key={p.id}
              aria-labelledby={`plan-${p.id}`}
              className={cn("relative flex flex-col rounded-2xl border bg-card p-5 shadow-sm", p.highlight && "border-primary ring-2 ring-primary/30")}
            >
              {p.highlight && <span className="absolute -top-3 left-5 rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-primary-foreground">Most popular</span>}
              <h3 id={`plan-${p.id}`} className="font-display text-xl font-semibold">
                {p.name}
              </h3>
              <p className="text-sm text-muted-foreground">{p.tagline}</p>
              <p className="mt-4">
                <span className="font-display text-4xl font-semibold">{pr.main}</span> <span className="text-sm text-muted-foreground">{pr.sub}</span>
              </p>
              <ul className="mt-4 flex-1 space-y-2 text-sm">
                {p.features.filter(shown).map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden /> {f}
                  </li>
                ))}
              </ul>
              {!compact &&
                (current === p.id ? (
                  <p className="mt-5 rounded-md border px-3 py-2 text-center text-sm font-medium">Your plan</p>
                ) : (
                  <Button
                    className="mt-5"
                    variant={p.highlight ? "default" : "outline"}
                    onClick={() => {
                      const period = yearly ? "year" : "month";
                      track("plan_click", { plan: p.id, period });
                      if (p.id === "free") return me.data ? window.location.assign("/") : go("/signup");
                      // Signed in: straight to Stripe. Otherwise create the account first, then pay.
                      if (me.data) return void choosePlan(p.id, period);
                      go(`/signup?plan=${p.id}&period=${period}`);
                    }}
                  >
                    {p.id === "free" ? (me.data ? "Open BrainLog" : "Start free") : `Choose ${p.name}`}
                  </Button>
                ))}
            </article>
          );
        })}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex gap-3 rounded-2xl border bg-card p-5 shadow-sm">
          <Users className="mt-1 size-5 shrink-0 text-primary" aria-hidden />
          <div>
            <h3 className="font-semibold">
              {lab.name} · €{Math.round((lab.yearly ?? 0) / 12)} per person / month
            </h3>
            <p className="text-sm text-muted-foreground">
              {lab.tagline} {lab.features.join(". ")}. Billed yearly.{" "}
              <a className="text-primary underline underline-offset-2" href="mailto:contact@brainlog.net?subject=BrainLog%20Lab%20plan">
                Write to us
              </a>
              .
            </p>
          </div>
        </div>
        <div className="flex gap-3 rounded-2xl border bg-card p-5 shadow-sm">
          <GraduationCap className="mt-1 size-5 shrink-0 text-primary" aria-hidden />
          <div>
            <h3 className="font-semibold">Students · Researcher for €{STUDENT_DISCOUNT.yearly} a year</h3>
            <p className="text-sm text-muted-foreground">Over {STUDENT_DISCOUNT.percent}% off, with your confirmed university email address (Settings → Account).</p>
          </div>
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground">{PRICING_INTRO.footnote}</p>
    </div>
  );
}

/** A small, static picture of the app (no screenshot to keep up to date). */
function Preview() {
  return (
    <div aria-hidden className="relative mx-auto w-full max-w-xl">
      <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-primary/15 via-transparent to-primary/10 blur-2xl" />
      <div className="overflow-hidden rounded-2xl border bg-card shadow-xl">
        <div className="flex items-center gap-1.5 border-b bg-muted/50 px-4 py-2.5">
          <span className="size-2.5 rounded-full bg-muted-foreground/30" />
          <span className="size-2.5 rounded-full bg-muted-foreground/30" />
          <span className="size-2.5 rounded-full bg-muted-foreground/30" />
          <span className="ml-3 text-xs text-muted-foreground">Sleep & heat project · Reading notes</span>
        </div>
        <div className="space-y-3 p-5 text-left">
          <p className="font-display text-lg font-semibold">Night-time heat and sleep: what the study found</p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Warmer bedrooms shortened sleep by about 14 minutes per degree{" "}
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">Lind 2024, p. 6</span>, mostly in older participants{" "}
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">p. 8</span>.
          </p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {[
              ["Question", "Does indoor heat change sleep?"],
              ["Method", "Cohort, 412 homes"],
              ["Limits", "Self-reported sleep"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg border bg-background p-2">
                <p className="font-medium">{k}</p>
                <p className="text-muted-foreground">{v}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-lg border bg-background p-2 text-xs">
            <FileSearch className="size-4 text-primary" />
            <span className="flex-1">Every quote is checked against the PDF</span>
            <Check className="size-4 text-success" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function Landing() {
  // Counted once per visit when the prices come into view (only with the visitor's consent).
  useEffect(() => {
    const el = document.getElementById("pricing");
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        track("pricing_view");
        io.disconnect();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div className="min-h-full bg-background">
      <PublicHeader />
      <main>
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-12 md:grid-cols-2 md:pt-20">
          <div>
            <p className="font-serif text-lg italic text-muted-foreground">{APP_TAGLINE}</p>
            <h1 className="mt-2 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">{HERO.title}</h1>
            <p className="mt-5 max-w-lg text-lg text-muted-foreground">{HERO.text}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => go("/signup")}>
                Start free <ArrowRight />
              </Button>
              <Button size="lg" variant="outline" onClick={() => go("/login")}>
                Sign in
              </Button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">Free up to 1 GB. No card needed.</p>
            <a
              href={YOUTUBE_TOUR}
              target="_blank"
              rel="noopener"
              onClick={() => track("video_click", { video: "tour" })}
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              <PlayCircle className="size-5" aria-hidden /> Watch BrainLog in two minutes
            </a>
          </div>
          <Preview />
        </section>

        <section id="features" aria-labelledby="features-h" className="border-t bg-muted/30 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 id="features-h" className="text-center font-display text-3xl font-semibold">
              {FEATURES_INTRO.title}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">{FEATURES_INTRO.text}</p>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((f) => (
                <div key={f.title} className="rounded-2xl border bg-card p-5 shadow-sm">
                  <span className="inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-5" aria-hidden>
                    <f.icon />
                  </span>
                  <h3 className="mt-3 font-semibold">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.text}</p>
                </div>
              ))}
            </div>
            <ul aria-label="Also in BrainLog" className="mx-auto mt-6 flex max-w-4xl flex-wrap justify-center gap-2 text-sm">
              {FEATURES_MORE.map((m) => (
                <li key={m} className="inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 text-muted-foreground">
                  <Check className="size-3.5 text-primary" aria-hidden /> {m}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-center text-sm text-muted-foreground">
              <a
                href={YOUTUBE_CHANNEL}
                target="_blank"
                rel="noopener"
                onClick={() => track("video_click", { video: "channel" })}
                className="inline-flex items-center gap-2 font-medium text-primary underline-offset-4 hover:underline"
              >
                <MonitorPlay className="size-5" aria-hidden /> See each part in two minutes on our YouTube channel
              </a>
            </p>
          </div>
        </section>

        <section aria-labelledby="ai-h" className="py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 id="ai-h" className="text-center font-display text-3xl font-semibold">
              {AI_INTRO.title}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">{AI_INTRO.text}</p>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {AI_WORDS.map((w) => (
                <div key={w.word} className="rounded-2xl border bg-card p-5 shadow-sm">
                  <span className="inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-5" aria-hidden>
                    <w.icon />
                  </span>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-primary">{w.word}</p>
                  <h3 className="mt-1 font-semibold">{w.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{w.text}</p>
                </div>
              ))}
            </div>
            <div className="mx-auto mt-6 flex max-w-4xl flex-col items-start gap-x-8 gap-y-2 rounded-2xl border bg-card px-5 py-3 text-sm shadow-sm sm:flex-row sm:items-center sm:justify-center">
              {AI_PAID.map((a) => (
                <p key={a.text} className="flex items-center gap-2 text-muted-foreground">
                  <a.icon className="size-4 shrink-0 text-primary" aria-hidden /> {a.text}
                </p>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" aria-labelledby="pricing-h" className="scroll-mt-16 border-t bg-muted/30 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 id="pricing-h" className="text-center font-display text-3xl font-semibold">
              {PRICING_INTRO.title}
            </h2>
            <p className="mx-auto mb-8 mt-3 max-w-2xl text-center text-muted-foreground">{PRICING_INTRO.text}</p>
            {/* An offer from an announcement's link (?offer=CODE). */}
            <div className="mx-auto mb-6 max-w-3xl">
              <OfferBanner />
            </div>
            <Pricing />
          </div>
        </section>

        <section id="questions" aria-labelledby="faq-h" className="scroll-mt-16 py-16">
          <div className="mx-auto max-w-3xl px-5">
            <h2 id="faq-h" className="text-center font-display text-3xl font-semibold">
              Questions
            </h2>
            <div className="mt-8 divide-y rounded-2xl border bg-card shadow-sm">
              {FAQ.map((f) => (
                <details key={f.q} className="group px-5 py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
                    {f.q}
                    <span className="text-muted-foreground transition-transform group-open:rotate-45" aria-hidden>
                      +
                    </span>
                  </summary>
                  <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t bg-muted/30 py-16 text-center">
          <h2 className="font-display text-3xl font-semibold">Ready when you are.</h2>
          <p className="mt-2 text-muted-foreground">Create your account in under a minute.</p>
          <Button size="lg" className="mt-6" onClick={() => go("/signup")}>
            Start free <ArrowRight />
          </Button>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
